/**
 * Zustand store for discovery state management
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { DiscoveryActions, DiscoveryState } from "../types";

/**
 * Combined discovery store type
 */
export type DiscoveryStore = DiscoveryState & DiscoveryActions;

/**
 * Initial state for discovery store
 */
const initialState: DiscoveryState = {
  currentUsers: [],
  swipedUsers: [],
  matches: [],
  isLoading: false,
  error: null,
};

/**
 * Discovery store for managing discovery state
 */
export const useDiscoveryStore = create<DiscoveryState & DiscoveryActions>()(
  devtools(
    (set, _get) => ({
      ...initialState,

      /**
       * Set the current users to display
       */
      setCurrentUsers: (users) => {
        set({ currentUsers: users }, false, "setCurrentUsers");
      },

      /**
       * Add a user to the swiped list
       */
      addSwipedUser: (userId) => {
        set(
          (state) => ({
            swipedUsers: [...state.swipedUsers, userId],
          }),
          false,
          "addSwipedUser"
        );
      },

      /**
       * Add a new match
       */
      addMatch: (match) => {
        set(
          (state) => ({
            matches: [...state.matches, match],
          }),
          false,
          "addMatch"
        );
      },

      /**
       * Set loading state
       */
      setLoading: (isLoading) => {
        set({ isLoading }, false, "setLoading");
      },

      /**
       * Set error state and clear loading
       */
      setError: (error) => {
        set({ error, isLoading: false }, false, "setError");
      },

      /**
       * Reset discovery state
       */
      reset: () => {
        set(initialState, false, "reset");
      },
    }),
    {
      name: "discovery-store",
    }
  )
);
